import type { ChangeEvent } from "react";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import {
  BOX_ID_PATTERN,
  ENTITY_ID_EXTRACT_PATTERN,
  PRODUCT_ID_PATTERN,
} from "../domain/entity-ids";
import { extractQrPayload, qrScanErrorToMessage } from "../domain/qr-payload";
import { Box } from "../stores/Box";
import { Product } from "../stores/Product";
import { STORE_TOKEN } from "../stores/store";
import type { IStore } from "../stores/store";
import {
  NAVIGATION_STORE_TOKEN,
  type INavigationStore,
  Screens,
} from "../stores/navigation";
import { WAREHOUSE_API_TOKEN, type IWarehouseApi } from "../api/WarehouseApi";
import type { InputOnChangeData } from "../components/Input/Input";

type QRNavigableEntity = Product | Box;
type FacingMode = "environment" | "user";

function extractEntityId(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const decoded = (() => {
    try {
      return decodeURIComponent(trimmed);
    } catch {
      return trimmed;
    }
  })();

  if (PRODUCT_ID_PATTERN.test(decoded) || BOX_ID_PATTERN.test(decoded)) {
    return decoded;
  }

  const match = decoded.match(ENTITY_ID_EXTRACT_PATTERN);
  if (!match) {
    return null;
  }

  return match[0];
}

function entityKind(entity: QRNavigableEntity): "product" | "box" {
  return PRODUCT_ID_PATTERN.test(entity.id) ? "product" : "box";
}

@injectable()
export class QRViewModel extends ViewModel {
  @observable
  isScannerEnabled = true;

  @observable
  facingMode: FacingMode = "environment";

  @observable
  isResolving = false;

  @observable
  scanError: string | null = null;

  @observable
  statusText = "Наведите камеру на QR-код";

  @observable
  manualCode = "";

  private lastResolvedId = "";

  private lastResolvedAt = 0;

  constructor(
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get results(): QRNavigableEntity[] {
    return this.store.QRResult;
  }

  @computed
  get hasResults(): boolean {
    return this.results.length > 0;
  }

  @computed
  get target(): QRNavigableEntity | null {
    return this.results[this.results.length - 1] || null;
  }

  @computed
  get scannerDelay(): number | false {
    if (!this.isScannerEnabled || this.isResolving) {
      return false;
    }
    return 300;
  }

  @action.bound
  toggleScanner(): void {
    this.isScannerEnabled = !this.isScannerEnabled;
    this.scanError = null;
    this.statusText = this.isScannerEnabled
      ? "Сканер включен"
      : "Сканер остановлен";
  }

  @action.bound
  switchCamera(): void {
    this.facingMode =
      this.facingMode === "environment" ? "user" : "environment";
    this.scanError = null;
    this.statusText =
      this.facingMode === "environment"
        ? "Используется задняя камера"
        : "Используется фронтальная камера";
  }

  @action.bound
  onManualCodeChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.manualCode = data.value || event.target.value;
    this.scanError = null;
  }

  @action.bound
  async resolveManualCode(): Promise<void> {
    await this.resolveCode(this.manualCode, "manual");
  }

  @action.bound
  clearResult(): void {
    this.store.getFromId("");
    this.scanError = null;
    this.statusText = "Результаты очищены";
    this.manualCode = "";
    this.lastResolvedId = "";
    this.lastResolvedAt = 0;
  }

  @action.bound
  async onScan(data: string | null): Promise<void> {
    if (!data) {
      return;
    }
    await this.resolveCode(data, "scanner");
  }

  @action.bound
  onError(error: unknown): void {
    this.scanError = qrScanErrorToMessage(error);
  }

  @action.bound
  goToQrManagement(): void {
    this.navigationStore.setScreen(Screens.QRManagement);
  }

  @action.bound
  openTarget(): void {
    if (!this.target) {
      return;
    }
    this.openEntity(this.target);
  }

  @action.bound
  openEntity(entity: QRNavigableEntity): void {
    if (entityKind(entity) === "product") {
      this.navigationStore.setScreen(Screens.Product, entity);
      return;
    }
    this.navigationStore.setScreen(Screens.Box, entity);
  }

  private async resolveCode(
    rawCode: string,
    source: "scanner" | "manual",
  ): Promise<void> {
    const entityId = await this.resolveEntityIdFromCode(rawCode);
    if (!entityId) {
      this.scanError =
        "QR-код не распознан. Укажите id вида prod_/box_ или привяжите qr_... к сущности.";
      return;
    }

    const now = Date.now();
    if (
      source === "scanner" &&
      entityId === this.lastResolvedId &&
      now - this.lastResolvedAt < 1200
    ) {
      return;
    }

    runInAction(() => {
      this.isResolving = true;
      this.scanError = null;
      this.statusText = `Проверяем ${entityId}...`;
    });

    try {
      await this.ensureEntityInStore(entityId);
      this.store.getFromId(entityId);

      const result = this.store.getFromIdResult as QRNavigableEntity | null;
      if (!result) {
        runInAction(() => {
          this.scanError = `Сущность с id ${entityId} не найдена`;
          this.statusText = "Ничего не найдено";
        });
        return;
      }

      runInAction(() => {
        this.manualCode = entityId;
        this.statusText = `Найдено: ${result.name} (${result.id})`;
        this.lastResolvedId = entityId;
        this.lastResolvedAt = Date.now();
      });
    } catch (error) {
      runInAction(() => {
        this.scanError =
          error instanceof Error
            ? error.message
            : "Не удалось обработать QR-код";
      });
    } finally {
      runInAction(() => {
        this.isResolving = false;
      });
    }
  }

  private async resolveEntityIdFromCode(
    rawCode: string,
  ): Promise<string | null> {
    const directEntityId = extractEntityId(rawCode);
    if (directEntityId) {
      return directEntityId;
    }

    const qrPayload = extractQrPayload(rawCode);
    if (!qrPayload) {
      return null;
    }

    const resolved = await this.api.resolveQrPayload(qrPayload);
    return resolved?.entityId || null;
  }

  private async ensureEntityInStore(entityId: string): Promise<void> {
    this.store.getFromId(entityId);
    if (this.store.getFromIdResult) {
      return;
    }

    if (PRODUCT_ID_PATTERN.test(entityId)) {
      const product = await this.api.getProductById(entityId);
      if (
        product &&
        !this.store.products.find((item) => item.id === product.id)
      ) {
        this.store.addProduct(new Product(product));
      }
      return;
    }

    if (BOX_ID_PATTERN.test(entityId)) {
      const box = await this.api.getBoxById(entityId);
      if (box && !this.store.boxes.find((item) => item.id === box.id)) {
        this.store.addBox(new Box(box));
      }
    }
  }
}

export type { QRNavigableEntity };
